import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Checkbox,
  TablePagination,
  Grid,
  Typography,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  Search,
  FilterList,
  FileDownload,
  Inventory,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import apiService, { Item, Stock, Category } from '../../services/apiService';
import SettingsSidebar from '../../components/Common/SettingsSidebar';

type CurrentView = 'items' | 'stocks';

// Component to display available stock for an item
const AvailableStockDisplay: React.FC<{ itemName: string }> = ({ itemName }) => {
  const [availableStock, setAvailableStock] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStock = async () => {
      try {
        const response = await apiService.checkStockAvailability(itemName);
        setAvailableStock(response.data.available_stock);
      } catch (error) {
        console.error('Failed to fetch stock:', error);
        setAvailableStock(0);
      } finally {
        setLoading(false);
      }
    };

    fetchStock();
  }, [itemName]);

  if (loading) {
    return <span style={{ color: '#6b7280' }}>Loading...</span>;
  }

  const stockColor = availableStock === 0 ? '#dc2626' : availableStock && availableStock < 10 ? '#f59e0b' : '#059669';
  
  return (
    <span style={{ 
      color: stockColor, 
      fontWeight: 500,
      fontSize: '14px'
    }}>
      {availableStock !== null ? availableStock : 0}
    </span>
  );
};

const StockManagement: React.FC = () => {
  const [currentView, setCurrentView] = useState<CurrentView>('stocks');
  const [items, setItems] = useState<Item[]>([]);
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  
  // Drawer states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<any>({});

  // Dropdown data
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    fetchData();
    fetchDropdownData();
  }, [currentView]);

  // Date formatting utility
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    };
    return date.toLocaleDateString('en-GB', options);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (currentView === 'items') {
        const response = await apiService.getItems();
        setItems(response.data);
      } else if (currentView === 'stocks') {
        const response = await apiService.getStocks();
        setStocks(response.data);
      }
    } catch (error: any) {
      console.error('Fetch error:', error); // Debug log
      toast.error(error.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const response = await apiService.getStockDropdownData();
      setSuppliers(response.data.suppliers || []);
      
      // Fetch items for the dropdown
      const itemsResponse = await apiService.getItems();
      setItems(itemsResponse.data || []);
      
      // Fetch categories
      const categoriesResponse = await apiService.getCategories();
      setCategories(categoriesResponse.data);
    } catch (error: any) {
      console.error('Failed to fetch dropdown data:', error);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, item: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = (item?: any) => {
    setAnchorEl(null);
    const itemToDelete = item || selectedItem;
    if (itemToDelete) {
      Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            if (currentView === 'items') {
              await apiService.deleteItem(itemToDelete.id);
            } else if (currentView === 'stocks') {
              await apiService.deleteStock(itemToDelete.id);
            }
            toast.success('Item deleted successfully');
            fetchData();
          } catch (error: any) {
            toast.error(error.message || 'Failed to delete item');
          }
        }
      });
    }
  };

  const handleEditClick = (item?: any) => {
    const itemToEdit = item || selectedItem;
    if (itemToEdit) {
      setEditingItemId(itemToEdit.id);
      setFormData(itemToEdit);
      setIsEditing(true);
      setDrawerOpen(true);
      setAnchorEl(null);
    }
  };

  const handleAddNew = async () => {
    if (currentView === 'stocks') {
      setFormData({
        transaction_type: 'in',
        stock_date: new Date().toISOString().split('T')[0], // Today's date
      });
    } else {
      setFormData({
        status: 'active',
        unit: 'pcs',
      });
    }
    setIsEditing(false);
    setDrawerOpen(true);
    
    // If we're adding stock, make sure we have dropdown data loaded
    if (currentView === 'stocks') {
      await fetchDropdownData();
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateSubmit = async () => {
    try {
      console.log('Creating:', currentView, formData);
      
      if (currentView === 'items') {
        // Validate required fields for items
        if (!formData.name || !formData.item_code) {
          toast.error('Please fill in all required fields');
          return;
        }
        await apiService.createItem(formData);
        toast.success('Item created successfully');
      } else if (currentView === 'stocks') {
        // Validate required fields for stocks
        if (!formData.item_id || !formData.quantity || !formData.transaction_type || !formData.stock_date) {
          toast.error('Please fill in all required fields');
          return;
        }
        await apiService.createStock(formData);
        toast.success('Stock created successfully');
      }
      
      setDrawerOpen(false);
      fetchData();
    } catch (error: any) {
      console.error('Create error:', error);
      toast.error(error.message || `Failed to create ${currentView === 'items' ? 'item' : 'stock'}`);
    }
  };

  const handleUpdateSubmit = async () => {
    if (!editingItemId) return;
    
    try {
      if (currentView === 'items') {
        await apiService.updateItem(editingItemId, formData);
      } else if (currentView === 'stocks') {
        await apiService.updateStock(editingItemId, formData);
      }
      toast.success('Item updated successfully');
      setDrawerOpen(false);
      setEditingItemId(null);
      fetchData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update item');
    }
  };

  const handleClose = () => {
    setDrawerOpen(false);
    setEditingItemId(null);
    setFormData({});
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  const filteredData = () => {
    if (currentView === 'items') {
      let data = items;
      if (searchTerm) {
        data = data.filter((item: Item) => 
          item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.item_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.category?.name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      return data;
    } else {
      let data = stocks;
      if (searchTerm) {
        data = data.filter((stock: Stock) => 
          stock.item?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          stock.item?.item_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          stock.supplier?.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          stock.reference_number?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      return data;
    }
  };

  const renderTable = () => {
    if (currentView === 'items') {
      const data = filteredData() as Item[];
      const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

      return (
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
              <TableCell padding="checkbox">
                <Checkbox />
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Item Name</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Item Code</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Unit Price</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Available Stock</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Loading items...
                </TableCell>
              </TableRow>
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No items found
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((item: Item) => {
                return (
                <TableRow key={item.id} hover sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                  <TableCell padding="checkbox">
                    <Checkbox />
                  </TableCell>
                  <TableCell sx={{ color: '#374151' }}>{item.name}</TableCell>
                  <TableCell sx={{ color: '#374151' }}>{item.item_code}</TableCell>
                  <TableCell sx={{ color: '#374151' }}>
                    {item.category ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '2px',
                            backgroundColor: item.category.color || '#4A90E2',
                          }}
                        />
                        {item.category.name}
                      </Box>
                    ) : 'N/A'}
                  </TableCell>
                  <TableCell sx={{ color: '#374151' }}>AED {parseFloat(String(item.unit_price || 0)).toFixed(2)}</TableCell>
                  <TableCell sx={{ color: '#374151' }}>
                    <AvailableStockDisplay itemName={item.name} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.status}
                      size="small"
                      sx={{
                        backgroundColor: item.status === 'active' ? '#e0f2f1' : '#fee2e2',
                        color: item.status === 'active' ? '#0d9488' : '#dc2626',
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleEditClick(item)}
                        sx={{
                          backgroundColor: '#e0f2f1',
                          color: '#0d9488',
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: '#ccedea',
                          },
                        }}
                      >
                        Update
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleDeleteClick(item)}
                        sx={{
                          backgroundColor: '#fee2e2',
                          color: '#dc2626',
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: '#fecaca',
                          },
                        }}
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      );
    } else {
      const data = filteredData() as Stock[];
      const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

      return (
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
              <TableCell padding="checkbox">
                <Checkbox />
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Item</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Supplier</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Quantity</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Transaction Type</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading stocks...
                </TableCell>
              </TableRow>
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No stocks found
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((stock: Stock) => (
                <TableRow key={stock.id} hover sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                  <TableCell padding="checkbox">
                    <Checkbox />
                  </TableCell>
                  <TableCell sx={{ color: '#374151' }}>
                    {stock.item?.name} ({stock.item?.item_code})
                  </TableCell>
                  <TableCell sx={{ color: '#374151' }}>
                    {stock.supplier?.supplier_name || 'N/A'}
                  </TableCell>
                  <TableCell sx={{ color: '#374151' }}>{stock.quantity}</TableCell>
                  <TableCell>
                    <Chip
                      label={stock.transaction_type}
                      size="small"
                      sx={{
                        backgroundColor: stock.transaction_type === 'in' ? '#e0f2f1' : '#fee2e2',
                        color: stock.transaction_type === 'in' ? '#0d9488' : '#dc2626',
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: '#374151' }}>{formatDate(stock.stock_date)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleEditClick(stock)}
                        sx={{
                          backgroundColor: '#e0f2f1',
                          color: '#0d9488',
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: '#ccedea',
                          },
                        }}
                      >
                        Update
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleDeleteClick(stock)}
                        sx={{
                          backgroundColor: '#fee2e2',
                          color: '#dc2626',
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: '#fecaca',
                          },
                        }}
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      );
    }
  };

  const renderFormFields = () => {
    if (currentView === 'items') {
      return (
        <>
          <TextField
            fullWidth
            label="Item Name"
            value={formData.name || ''}
            onChange={(e) => handleFormChange('name', e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Item Code"
            value={formData.item_code || ''}
            onChange={(e) => handleFormChange('item_code', e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description || ''}
            onChange={(e) => handleFormChange('description', e.target.value)}
            margin="normal"
            multiline
            rows={3}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.category_id || ''}
              onChange={(e) => handleFormChange('category_id', e.target.value)}
              label="Category"
            >
              <SelectMenuItem value="">None</SelectMenuItem>
              {categories.map((category) => (
                <SelectMenuItem key={category.id} value={category.id}>
                  {category.name}
                </SelectMenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Unit Price"
            type="number"
            value={formData.unit_price || ''}
            onChange={(e) => handleFormChange('unit_price', parseFloat(e.target.value) || 0)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Unit"
            value={formData.unit || 'pcs'}
            onChange={(e) => handleFormChange('unit', e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Reorder Level"
            type="number"
            value={formData.reorder_level || ''}
            onChange={(e) => handleFormChange('reorder_level', parseInt(e.target.value) || 0)}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status || 'active'}
              onChange={(e) => handleFormChange('status', e.target.value)}
            >
              <SelectMenuItem value="active">Active</SelectMenuItem>
              <SelectMenuItem value="inactive">Inactive</SelectMenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Notes"
            value={formData.notes || ''}
            onChange={(e) => handleFormChange('notes', e.target.value)}
            margin="normal"
            multiline
            rows={3}
          />
        </>
      );
    } else {
      return (
        <>
          <FormControl fullWidth margin="normal">
            <InputLabel>Item</InputLabel>
            <Select
              value={formData.item_id || ''}
              onChange={(e) => handleFormChange('item_id', e.target.value)}
              label="Item"
            >
              {items.map((item) => (
                <SelectMenuItem key={item.id} value={item.id}>
                  {item.name} ({item.item_code})
                </SelectMenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Supplier (Optional)</InputLabel>
            <Select
              value={formData.supplier_id || ''}
              onChange={(e) => handleFormChange('supplier_id', e.target.value)}
            >
              <SelectMenuItem value="">None</SelectMenuItem>
              {suppliers.map((supplier) => (
                <SelectMenuItem key={supplier.id} value={supplier.id}>
                  {supplier.supplier_name}
                </SelectMenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Quantity"
            type="number"
            value={formData.quantity || ''}
            onChange={(e) => handleFormChange('quantity', parseInt(e.target.value) || 0)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Unit Cost"
            type="number"
            value={formData.unit_cost || ''}
            onChange={(e) => handleFormChange('unit_cost', parseFloat(e.target.value) || 0)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Stock Date"
            type="date"
            value={formData.stock_date || ''}
            onChange={(e) => handleFormChange('stock_date', e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Transaction Type</InputLabel>
            <Select
              value={formData.transaction_type || 'in'}
              onChange={(e) => handleFormChange('transaction_type', e.target.value)}
              label="Transaction Type"
            >
              <SelectMenuItem value="in">Stock In</SelectMenuItem>
              <SelectMenuItem value="out">Stock Out</SelectMenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Reference Number"
            value={formData.reference_number || ''}
            onChange={(e) => handleFormChange('reference_number', e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Notes"
            value={formData.notes || ''}
            onChange={(e) => handleFormChange('notes', e.target.value)}
            margin="normal"
            multiline
            rows={3}
          />
        </>
      );
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1f2937', fontSize: '2rem' }}>
          Stock Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Inventory />}
          onClick={handleAddNew}
          sx={{ 
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            backgroundColor: '#4A90E2',
            color: 'white',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#3b82f6',
            },
          }}
        >
          New {currentView === 'items' ? 'Item' : 'Stock'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Sidebar - Stock Menu */}
        <Grid size={{ xs: 12, md: 3 }}>
          <SettingsSidebar 
            variant="stock" 
            currentView={currentView}
            onViewChange={(view: any) => setCurrentView(view)}
          />
        </Grid>

        {/* Right Content Area */}
        <Grid size={{ xs: 12, md: 9 }}>
          {/* Overview Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em'
                  }}>
                    Total {currentView === 'items' ? 'Items' : 'Stocks'}
                  </Typography>
                  <Typography variant="h2" sx={{ 
                    color: '#1e40af', 
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}>
                    {currentView === 'items' ? items.length : stocks.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em'
                  }}>
                    Active {currentView === 'items' ? 'Items' : 'Stocks'}
                  </Typography>
                  <Typography variant="h2" sx={{ 
                    color: '#1e40af', 
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}>
                    {currentView === 'items' ? items.filter(i => i.status === 'active').length :
                     stocks.filter(s => s.transaction_type === 'in').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em'
                  }}>
                    New {currentView === 'items' ? 'Items' : 'Stocks'} (This Month)
                  </Typography>
                  <Typography variant="h2" sx={{ 
                    color: '#1e40af', 
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}>
                    {(() => {
                      if (currentView === 'items') {
                        return items.filter(item => {
                          const itemDate = new Date(item.created_at);
                          const currentDate = new Date();
                          return itemDate.getMonth() === currentDate.getMonth() && 
                                 itemDate.getFullYear() === currentDate.getFullYear();
                        }).length;
                      } else {
                        return stocks.filter(stock => {
                          const stockDate = new Date(stock.created_at);
                          const currentDate = new Date();
                          return stockDate.getMonth() === currentDate.getMonth() && 
                                 stockDate.getFullYear() === currentDate.getFullYear();
                        }).length;
                      }
                    })()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              {/* Search and Action Bar */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    placeholder="Search here"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: '#4A90E2' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      width: 400,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#e0f2fe',
                        borderColor: '#e0f2fe',
                        '& fieldset': {
                          borderColor: '#e0f2fe',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bae6fd',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4A90E2',
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: '#374151',
                        '&::placeholder': {
                          color: '#9ca3af',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    sx={{ 
                      px: 3,
                      py: 1.5,
                      borderRadius: '12px',
                      borderColor: '#e0f2fe',
                      backgroundColor: 'white',
                      color: '#4A90E2',
                      fontWeight: 500,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#bae6fd',
                        backgroundColor: '#f0f9ff',
                      },
                    }}
                  >
                    Filter
                  </Button>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<FileDownload />}
                  sx={{ 
                    px: 3,
                    py: 1.5,
                    borderRadius: '12px',
                    backgroundColor: '#4A90E2',
                    color: 'white',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#3b82f6',
                    },
                  }}
                >
                  Export
                </Button>
              </Box>

              {/* Data Table */}
              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb', height: 'calc(100vh - 450px)' }}>
                {renderTable()}
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredData().length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Showing"
                labelDisplayedRows={({ from, to, count }) =>
                  `Showing ${from} to ${to} out of ${count} records`
                }
                sx={{
                  '& .MuiTablePagination-selectLabel': {
                    color: '#6b7280',
                  },
                  '& .MuiTablePagination-displayedRows': {
                    color: '#6b7280',
                  },
                  '& .MuiTablePagination-actions': {
                    '& .MuiIconButton-root': {
                      color: '#6b7280',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                      },
                    },
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 400,
            padding: 3,
          },
        }}
      >
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          {isEditing ? 'Edit' : 'Add'} {currentView === 'items' ? 'Item' : 'Stock'}
        </Typography>
        
        {renderFormFields()}
        
        <Box sx={{ display: 'flex', gap: 2, marginTop: 3 }}>
          <Button variant="outlined" onClick={handleClose} fullWidth>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={isEditing ? handleUpdateSubmit : handleCreateSubmit}
            fullWidth
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </Box>
      </Drawer>

      {/* Toast Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Box>
  );
};

export default StockManagement;